import { required, email, minLength } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import type { SignUpFormData } from "~/types/account";
import { buildSignUpPayload } from "~/utils/account/buildSignUpPayload";
import { loginRedirectPath } from "~/layers/base/utils/account/loginRedirectPath";
import { isSignUpOpen } from "~/layers/base/utils/account/isSignUpOpen";
import { norceErrorMessage } from "~/layers/base/utils/account/norceErrorMessage";

/** Form state, validation and submit for {@link AccountSignUpForm}. */
export const useAccountSignUp = () => {
	const { $toast } = useNuxtApp();
	const { createAccount, login } = useNorcePwa();
	// Optional calls below: breadandboxers forks useGtmEvents without signUp or
	// login. Its own AccountSignUpForm fork bypasses this composable today, so
	// the calls are unreachable there — but base must not assume what a fork
	// provides.
	const { signUp, login: gtmLogin } = useGtmEvents();
	const { executeRecaptcha } = useRecaptcha();
	const { applicationNorce } = storeToRefs(useNorceStore());
	const {
		public: {
			norce: { b2bAccess, requireLogin }
		}
	} = useRuntimeConfig();
	const signUpOpen = isSignUpOpen({ requireLogin, b2bAccess });
	const localePath = useLocalePath();
	const router = useRouter();
	const route = useRoute();
	const { t, locale } = useI18n();

	// create-account sets no session cookie, so sign in to get one.
	const signInNewCustomer = async () => {
		const redirect = loginRedirectPath(route.query.redirect);
		let user: Awaited<ReturnType<typeof login>> | undefined;
		try {
			user = await login({
				loginName: formData.value.Email,
				password: formData.value.Password,
				recaptchaToken: await executeRecaptcha("login"),
				localeCode: locale.value
			});
		} catch (error) {
			// Status only: the failed request's URL carries the password.
			const { statusCode } = (error ?? {}) as { statusCode?: number };
			console.warn(
				"[useAccountSignUp] sign-in after sign-up failed",
				statusCode
			);
		}

		// The account exists either way; without a session, log in by hand.
		if (!user) {
			$toast.info(t("account.signup_created_login"));
			return router.push({
				path: localePath("/login"),
				query: redirect ? { redirect } : {}
			});
		}

		userStore().setUser(user);
		$toast.success(t("successfulLogin"));
		gtmLogin?.();
		return router.push(redirect ?? localePath("/account"));
	};

	const countries = applicationNorce?.value?.Countries;
	const loading = ref(false);

	const countryOptions = computed(
		() =>
			countries?.List?.map((country) => ({
				label: country.Name,
				value: country.Id
			})) ?? []
	);

	const formData = ref<SignUpFormData>({
		FirstName: "",
		LastName: "",
		Email: "",
		Password: "",
		agree: false,
		Account: {
			LoginName: "",
			Name: "",
			Roles: null,
			IsActive: true
		},
		InvoiceAddress: {
			Line1: "",
			Zip: "",
			City: "",
			CountryId: 0,
			Country: "Sverige"
		},
		Company: {
			Name: "",
			OrgNo: "",
			VatNo: "",
			Email: "",
			Phone: ""
		}
	});

	const rules = computed(() => ({
		FirstName: { required, minLength: minLength(3) },
		LastName: { required, minLength: minLength(3) },
		Email: { required, email },
		Password: { required, minLength: minLength(8) },
		Account: {
			LoginName: { required, minLength: minLength(3) }
		},
		InvoiceAddress: {
			Line1: { required, minLength: minLength(3) },
			// Norce's RegisterCustomer requires a postal code on the invoice address
			// (empty → 400 "Postnummer saknas"), so Zip stays required. The fixed
			// 5–6 length lock is removed so international postal codes are accepted.
			Zip: { required },
			City: { required },
			CountryId: { required }
		},
		agree: {
			checked: (value: boolean) => value === true
		},
		...(b2bAccess
			? {
					Company: {
						Name: { required },
						OrgNo: { required },
						VatNo: { required },
						Email: { required, email },
						Phone: { required }
					}
				}
			: {})
	}));

	const $v = useVuelidate<SignUpFormData>(rules, formData);

	/** Resolves to `true` when the account was created. */
	const submit = async () => {
		if (!signUpOpen) {
			$toast.error(t("account.signup_closed"));
			return false;
		}

		$v.value.$touch();

		if (!(await $v.value.$validate())) return false;

		try {
			loading.value = true;

			// Dead as written: `Account.Name` starts empty, so this never runs.
			// Kept verbatim by the refactor.
			if (
				formData.value.Account?.Name &&
				formData.value.InvoiceAddress?.Country
			) {
				formData.value.Account.Name = `${formData.value.FirstName} ${formData.value.LastName}`;

				const countryName = countries?.List?.find(
					(country) => country.Id === formData.value.InvoiceAddress?.CountryId
				)?.Name;

				if (countryName) {
					formData.value.InvoiceAddress.Country = countryName;
				}
			}

			const recaptchaToken = await executeRecaptcha("signup");

			const payload = buildSignUpPayload(formData.value, b2bAccess);

			const user = await createAccount(
				{
					password: formData.value.Password,
					customer: formData.value.Email,
					localeCode: locale.value
				},
				payload,
				recaptchaToken
			);

			// Fires for B2B too — the account was created, approval is a later step.
			signUp?.();

			// Pending approval cannot log in, and signing it in would reveal prices.
			if (b2bAccess) {
				$toast.success(t("account.b2b_signup_received"));
			} else if (user) {
				// The account exists: a failure from here on is not a failed sign-up.
				await signInNewCustomer().catch((error: unknown) => {
					console.warn(
						"[useAccountSignUp] after sign-up:",
						(error as { message?: string } | undefined)?.message
					);
					$toast.info(t("account.signup_created_login"));
				});
			}

			return true;
		} catch (error) {
			const message = norceErrorMessage(error, "Account register error");

			if (isNorcePwaError(error)) {
				// Norce localises its texts by cultureCode — map the ones we know.
				const localised: Record<string, string> = {
					[ErrorStatusMessage.RECAPTCHA_FAILED]: t("recaptcha_failed"),
					[ErrorStatusMessage.SIGN_UP_CLOSED]: t("account.signup_closed"),
					[ErrorStatusMessage.COMPANY_ORG_NO_EXISTS]: t(
						"account.company_org_no_exists"
					)
				};

				$toast.error(localised[message] ?? message);
			}

			return false;
		} finally {
			loading.value = false;
		}
	};

	watch(
		() => formData.value.Email,
		(newEmail: string) => {
			formData.value.Account.LoginName = newEmail || "";
		}
	);

	return { formData, $v, loading, countryOptions, b2bAccess, submit };
};
